import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  Chip,
  alpha,
  Divider,
  IconButton,
  Switch,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  CircularProgress,
  Tooltip,
  Checkbox,
  Stepper,
  Step,
  StepLabel
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import NotesIcon from "@mui/icons-material/Notes";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import QrCodeIcon from "@mui/icons-material/QrCode";
import WarningIcon from "@mui/icons-material/Warning";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AsyncSelect from 'react-select/async';
import VariantSelector from '../../product/components/VariantSelector';
import IMEISerialDialog from '../../product/components/IMEISerialDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import { searchProducts, addProduct, getProductById } from "../../product/services/productService";
import { 
  addPurchaseInvoice, 
  updatePurchaseInvoice, 
  getPurchaseInvoiceById,
  downloadPurchaseOrderTemplate,
  validatePurchaseOrderImportFile,
  confirmPurchaseOrderImport
} from "../services/purchaseService";
import { getSuppliers, searchSuppliersByName, addSupplier } from "../../suppliers/services/supplierService";
import { getVatRates } from "../../companysettings/services/vatService";
import { getStoreNames } from "../../stores/services/storeService";
import SupplierSelect from "../../suppliers/components/SupplierSelect";
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DocumentProductsTable from '../../../components/shared/DocumentProductsTable';

function AddPurchasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingInvoiceId = searchParams.get('id'); // Check if we're editing an existing invoice
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  
  // Get VAT rates from Redux store
  const vatRatesFromStore = useSelector((state) => state.vat?.vatRates || []);
  const [vatRates, setVatRates] = useState([]);
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({ 
    Name: "", 
    SKU: "", 
    SalePrice: "", 
    PurchasePrice: "" 
  });
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState("");
  const [taxRate, setTaxRate] = useState(21); // Default 21% VAT
  
  // Invoice header state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [referenceNumberError, setReferenceNumberError] = useState(false);
  const [notes, setNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [shipmentCost, setShipmentCost] = useState(0); // ✅ Shipment/Delivery cost
  
  // Line items
  const [lineItems, setLineItems] = useState([]);
  
  // Mirrors referenceNumber — set when user types the supplier's invoice number
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Flags
  const [isPriceIncludeVAT, setIsPriceIncludeVAT] = useState(true); // Price includes VAT (default ON)
  const [documentStatus, setDocumentStatus] = useState('Draft'); // Document status: Draft or Posted
  const [isEUPurchase, setIsEUPurchase] = useState(false); // EU Purchase: VAT 0%
  const loadedIsICARef = useRef(null); // Tracks IsICA loaded from API to survive supplier auto-detect

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  // ✅ Store selection state
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeNames, setStoreNames] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);

  // Variant + IMEI state
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [imeiDialogOpen, setImeiDialogOpen] = useState(false);
  const [pendingImeiItems, setPendingImeiItems] = useState([]);
  
  // ✅ Computed: Determine if IMEI/Serial entry is required
  const requiresSerialNumbers = React.useMemo(() => {
    // Case 1: Variant is selected and is serialized
    if (selectedVariant?.isSerializedProduct) {
      return true;
    }
    // Case 2: Product without variant but is serialized
    if (!selectedVariant && selectedProduct?.IsSerializedProduct) {
      return true;
    }
    return false;
  }, [selectedVariant, selectedProduct]);
  
  // ✅ Dialog states
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '', address: '' });
  const [supplierFormTouched, setSupplierFormTouched] = useState(false);
  
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productFormTouched, setProductFormTouched] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // Product being edited
  const [editingProductLineItem, setEditingProductLineItem] = useState(null); // Line item being edited
  
  // ✅ File upload state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceFileName, setInvoiceFileName] = useState('');

  // ✅ Import dialog state
  const [importDialog, setImportDialog] = useState({
    open: false,
    file: null,
    fileName: '',
    loading: false,
    validating: false,
    validationResult: null,
    error: null,
    successMessage: null,
    selectedNewProductIndices: [], // Indices of selected new products
    activeStep: 0, // Stepper state: 0 = Upload & Validate, 1 = Import/Select Products, 2 = Review & Confirm
    importedLineItems: [] // Line items from import API response
  });

  // ✅ Auto-calculate totals
  const calculations = React.useMemo(() => {
    const effectivePriceIncludesVAT = isEUPurchase ? false : isPriceIncludeVAT;
    const subTotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
    
    // Calculate tax based on whether price includes VAT
    const taxAmount = lineItems.reduce((sum, item) => {
      let itemTax;
      if (effectivePriceIncludesVAT) {
        // VAT is included - extract it from the price
        itemTax = (item.totalPrice * item.taxRate) / (100 + item.taxRate);
      } else {
        // VAT not included - add it on top
        itemTax = (item.totalPrice * item.taxRate) / 100;
      }
      return sum + itemTax;
    }, 0);
    
    const totalAmount = effectivePriceIncludesVAT
      ? subTotal - discountAmount + Number(shipmentCost)  // VAT already included in subTotal, add shipment cost
      : subTotal + taxAmount - discountAmount + Number(shipmentCost);  // Add VAT on top, add shipment cost
    
    return {
      subTotal: subTotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      discountAmount: Number(discountAmount).toFixed(2),
      shipmentCost: Number(shipmentCost).toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  }, [lineItems, discountAmount, shipmentCost, isPriceIncludeVAT, isEUPurchase]);

  // Fetch suppliers, stores, and VAT rates on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await getSuppliers();
        const suppliersData = response?.payload || response || [];
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      } catch (error) {

        toast.error('Failed to load suppliers');
        setSuppliers([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    
    const fetchStores = async () => {
      setStoresLoading(true);
      try {
        const response = await getStoreNames();
        const stores = response?.payload || response || [];
        const storeList = Array.isArray(stores) ? stores : [];
        setStoreNames(storeList);
        // Auto-select first store if only one available
        if (storeList.length === 1) {
          setSelectedStore({ value: storeList[0].Id, label: storeList[0].StoreName });
        }
      } catch (error) {

        toast.error('Failed to load stores');
      } finally {
        setStoresLoading(false);
      }
    };
    
    const fetchVatRates = async () => {
      // ✅ Use VAT rates from Redux if available, otherwise fetch from API
      if (vatRatesFromStore && vatRatesFromStore.length > 0) {
        setVatRates(vatRatesFromStore);
      } else {
        try {
          const response = await getVatRates();
          const rates = response?.payload || response || [];
          setVatRates(Array.isArray(rates) ? rates : []);
        } catch (error) {

          // Use default rates if API fails
          setVatRates([]);
        }
      }
    };
    
    fetchSuppliers();
    fetchStores();
    fetchVatRates();
  }, []);

  // ✅ Load invoice data if editing
  useEffect(() => {
    const loadInvoice = async () => {
      if (!editingInvoiceId) return;
      
      try {
        setIsLoadingInvoice(true);
        const response = await getPurchaseInvoiceById(editingInvoiceId);
        const invoice = response?.payload || response;
        
        if (!invoice) {
          toast.error('Purchase invoice not found');
          navigate('/purchases');
          return;
        }
        
        // ✅ Only allow editing invoices in Draft status
        if (invoice.DocumentStatus !== 'Draft') {
          toast.warning('Only invoices in Draft status can be edited');
          navigate(`/purchases/${editingInvoiceId}`);
          return;
        }
        
        // Populate form fields
        const ref = invoice.ReferenceNumber || invoice.PurchaseInvoiceNumber || '';
        setReferenceNumber(ref);
        setInvoiceNumber(ref);
        
        // ✅ Extract date without timezone conversion to avoid date shifting
        setInvoiceDate(invoice.InvoiceDate ? invoice.InvoiceDate.slice(0, 10) : '');
        setDueDate(invoice.DueDate ? invoice.DueDate.slice(0, 10) : '');
        setNotes(invoice.Notes || '');
        setDiscountAmount(invoice.DiscountAmount || 0);
        setShipmentCost(invoice.ShipmentCost || 0); // ✅ Load shipment cost
        setDocumentStatus(invoice.DocumentStatus || 'Draft');

        // ✅ Map IsICA → EU Purchase toggle
        const icaValue = invoice.IsICA === true || invoice.IsICA === 'true' || invoice.IsICA === 1;
        loadedIsICARef.current = icaValue;
        setIsEUPurchase(icaValue);
        if (icaValue) setTaxRate(0);
        
        // Load store if available
        if (invoice.StoreId || invoice.storeId) {
          const storeId = invoice.StoreId || invoice.storeId;
          const storeName = invoice.StoreName || invoice.storeName || 'Store';
          setSelectedStore({
            value: storeId,
            label: storeName,
            isMain: invoice.IsMainStore || false,
            isVirtual: invoice.IsVirtualStore || false
          });
        }
        
        // Set supplier
        if (invoice.Supplier || invoice.SupplierId) {
          setSelectedSupplier({
            value: invoice.Supplier?.Id || invoice.SupplierId,
            label: invoice.Supplier?.Name || invoice.SupplierName || '',
            Name: invoice.Supplier?.Name || invoice.SupplierName,
            ...invoice.Supplier
          });
        }
        
        // Load line items
        if (invoice.LineItems && invoice.LineItems.length > 0) {
          const mappedLineItems = invoice.LineItems.map((item, index) => ({
            _rowKey: `draft_${item.Id || index}_${Date.now()}`,
            lineItemId: item.Id, // Store original line item ID for updates
            productId: item.ProductId,
            productName: item.ProductName || item.Product?.Name || 'Unknown Product',
            sku: item.ProductSKU || item.Product?.SKU || '',
            variantId: item.ProductVariantId || null,
            variantName: item.ProductVariant?.VariantName || null,
            hasVariants: item.HasVariants || false,
            isSerializedProduct: item.IsSerializedProduct || false,
            imeiItems: item.InventoryItems || [],
            quantity: item.Quantity || 1,
            unitPrice: item.UnitCost || 0,
            discountPercentage: item.DiscountPercentage || 0,
            discountAmount: item.DiscountAmount || 0,
            taxRate: item.TaxPercentage != null ? Number(item.TaxPercentage) : 21,
            totalPrice: item.LineTotal || 0,
            description: item.Description || ''
          }));
          setLineItems(mappedLineItems);
        }
        
        const loadMessage = invoice.DocumentStatus === 'Draft' 
          ? 'Draft invoice loaded successfully' 
          : 'Invoice loaded for editing';
        toast.success(loadMessage);
      } catch (error) {

        toast.error('Failed to load purchase invoice');
        navigate('/purchases');
      } finally {
        setIsLoadingInvoice(false);
      }
    };
    
    loadInvoice();
  }, [editingInvoiceId, navigate]);

  // Auto-set due date (30 days from invoice date)
  useEffect(() => {
    if (invoiceDate && !dueDate && !editingInvoiceId) { // Don't auto-set if editing
      const invoice = new Date(invoiceDate);
      const due = new Date(invoice);
      due.setDate(due.getDate() + 30);
      setDueDate(due.toISOString().slice(0, 10));
    }
  }, [invoiceDate, editingInvoiceId]);

  // Auto-detect EU Purchase based on supplier VAT number (non-BE = EU supplier)
  useEffect(() => {
    if (!selectedSupplier) return;
    // If we just loaded IsICA from the API, use that value and clear the ref
    if (loadedIsICARef.current !== null) {
      const ica = loadedIsICARef.current;
      setIsEUPurchase(ica);
      if (ica) {
        setTaxRate(0);
        // Zero out any line items already loaded whose taxRate was incorrectly defaulted
        setLineItems(prev => prev.map(item => ({ ...item, taxRate: 0 })));
      }
      loadedIsICARef.current = null;
      return;
    }
    const btwNumber = selectedSupplier.BTWNumber || '';
    if (btwNumber.trim() && !btwNumber.toUpperCase().startsWith('BE')) {
      setIsEUPurchase(true);
      setTaxRate(0);
      setLineItems(prev => prev.map(item => ({ ...item, taxRate: 0 })));
    } else {
      setIsEUPurchase(false);
    }
  }, [selectedSupplier]);

  // ✅ Debounced product search to prevent excessive API calls
  const loadProductOptions = React.useCallback((inputValue) => {
    return new Promise((resolve) => {
      if (!inputValue || inputValue.length < 2) {
        resolve([]);
        return;
      }

      // Clear previous timeout
      if (loadProductOptions.timeoutId) {
        clearTimeout(loadProductOptions.timeoutId);
      }

      // Set new timeout for debouncing (500ms)
      loadProductOptions.timeoutId = setTimeout(async () => {
        try {
          const barcodeLengths = [8, 12, 13, 14];
          let paramsObj;
          if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
            paramsObj = { skuBarCode: inputValue };
          } else if (/^\d+$/.test(inputValue)) {
            paramsObj = { skuBarCode: inputValue };
          } else {
            paramsObj = { name: inputValue };
          }
          const data = await searchProducts(paramsObj);
          const productsArray = Array.isArray(data?.payload) ? data.payload : Array.isArray(data) ? data : [];
          const options = productsArray.map((p) => ({
            value: p.Id,
            label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
            ...p,
          }));
          // ✅ Add "+ Add New Product" option after API response
          options.push({ value: "new", label: "+ Add New Product" });
          resolve(options);
        } catch (error) {

          resolve([]);
        }
      }, 500);
    });
  }, []);

  const loadSupplierOptions = async (inputValue) => {
    try {
      if (!inputValue || inputValue.trim().length < 3) return [];
      const response = await searchSuppliersByName(inputValue.trim());
      const searchResults = response?.payload || response || [];
      
      if (Array.isArray(searchResults)) {
        return searchResults.map(s => ({
          value: s.Id,
          label: s.Name,
          Name: s.Name,
          ...s
        }));
      }
      return [];
    } catch (error) {

      // ✅ Don't show toast here - let the SupplierSelect component handle it with debouncing
      return [];
    }
  };

  const handleProductSelect = (product) => {
    if (product && product.value === "new") {
      // Open product dialog instead of inline form
      setOpenProductDialog(true);
      setSelectedProduct(null);
    } else {
      setIsNewProduct(false);
      setSelectedProduct(product);
      // ✅ Clear unit price when product is selected
      setUnitPrice("");
      // Reset variant + IMEI when product changes
      setSelectedVariant(null);
      setPendingImeiItems([]);
    }
  };

  // ✅ Updated handleAddLineItem - merge products+variants if already exists
  const handleAddLineItem = async () => {
    let productId = selectedProduct?.Id || selectedProduct?.value;
    let productName = selectedProduct?.Name || newProductData.Name;
    let sku = selectedProduct?.SKU || newProductData.SKU;

    if (isNewProduct) {
      try {
        const response = await addProduct(newProductData);
        const newProd = response?.payload || response;
        productId = newProd.Id;
        productName = newProd.Name;
        sku = newProd.SKU;
      } catch (error) {

        toast.error('Failed to add new product');
        return;
      }
    }

    if (!productId || !quantity || !unitPrice) {
      toast.error('Please fill all product fields');
      return;
    }

    // ✅ Validate IMEI/Serial numbers for serialized products
    const isSerializedItem = selectedVariant?.isSerializedProduct || selectedProduct?.IsSerializedProduct;
    
    if (isSerializedItem) {
      const expectedCount = Number(quantity);
      const enteredCount = pendingImeiItems.length;
      
      if (enteredCount === 0) {
        const confirm = window.confirm(
          `⚠️ This ${selectedVariant ? 'variant' : 'product'} requires IMEI/Serial numbers.\n\n` +
          `You need to enter ${expectedCount} IMEI/Serial number(s).\n\n` +
          `Do you want to add them now? (Click Cancel to add without IMEI - can be added later)`
        );
        
        if (confirm) {
          setImeiDialogOpen(true);
          return;
        }
        // User chose to proceed without IMEI
        toast.warning('Item added without IMEI/Serial numbers. You can add them later.');
      } else if (enteredCount !== expectedCount) {
        toast.error(
          `IMEI/Serial number count mismatch!\n` +
          `Expected: ${expectedCount}, Entered: ${enteredCount}\n` +
          `Please enter exactly ${expectedCount} IMEI/Serial number(s).`
        );
        setImeiDialogOpen(true);
        return;
      }
    }

    const variantId = selectedVariant?.id || null;
    const variantName = selectedVariant?.variantName || null;

    // ✅ Check if same product+variant already exists in line items
    const existingItemIndex = lineItems.findIndex(
      item => item.productId === productId && item.variantId === variantId
    );

    if (existingItemIndex !== -1) {
      // ✅ Merge - update quantity and append IMEI items
      setLineItems(prev => {
        const updated = [...prev];
        const existingItem = updated[existingItemIndex];
        const newQuantity = existingItem.quantity + Number(quantity);
        const newTotalPrice = newQuantity * Number(unitPrice);
        
        // Merge IMEI items
        const mergedImeiItems = [...(existingItem.imeiItems || []), ...pendingImeiItems];
        
        updated[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          unitPrice: Number(unitPrice),
          taxRate: isEUPurchase ? 0 : Number(taxRate),
          totalPrice: newTotalPrice,
          imeiItems: mergedImeiItems,
        };
        return updated;
      });
      
      const variantDisplay = variantName ? ` • ${variantName}` : '';
      toast.success(
        `Quantity updated for ${productName}${variantDisplay}\n` +
        (pendingImeiItems.length > 0 ? `Added ${pendingImeiItems.length} IMEI/Serial numbers` : '')
      );
    } else {
      // ✅ New line item
      const validTaxRate = isEUPurchase ? 0 : Math.max(0, Math.min(100, Number(taxRate) || 21));
      const lineItem = {
        productId,
        productName,
        sku,
        variantId,
        variantName,
        hasVariants: selectedProduct?.HasVariants || false,
        isSerializedProduct: isSerializedItem,
        imeiItems: pendingImeiItems,
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        discountPercentage: 0,
        taxRate: validTaxRate,
        totalPrice: Number(quantity) * Number(unitPrice),
      };
      
      setLineItems(prev => [...prev, lineItem]);
      
      const variantDisplay = variantName ? ` • ${variantName}` : '';
      const imeiDisplay = pendingImeiItems.length > 0 ? ` (${pendingImeiItems.length} IMEI/Serial)` : '';
      toast.success(`Line item added: ${productName}${variantDisplay}${imeiDisplay}`);
    }

    // Reset product fields
    setSelectedProduct(null);
    setIsNewProduct(false);
    setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
    setQuantity(1);
    setUnitPrice("");
    setTaxRate(21);
    setSelectedVariant(null);
    setPendingImeiItems([]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    toast.info('Line item removed');
  };

  const purchaseDocumentProducts = React.useMemo(() => {
    const effectivePriceIncludesVAT = isEUPurchase ? false : isPriceIncludeVAT;
    return lineItems.map((item, index) => {
      const qty = Number(item.quantity || 0);
      const unitPriceValue = Number(item.unitPrice || 0);
      const taxRateValue = Number(item.taxRate || 0);
      const subtotal = qty * unitPriceValue;

      let totalExcl;
      let vatAmount;
      let totalIncl;

      if (effectivePriceIncludesVAT) {
        totalIncl = subtotal;
        totalExcl = taxRateValue > 0 ? subtotal / (1 + taxRateValue / 100) : subtotal;
        vatAmount = totalIncl - totalExcl;
      } else {
        totalExcl = subtotal;
        vatAmount = taxRateValue > 0 ? subtotal * (taxRateValue / 100) : 0;
        totalIncl = totalExcl + vatAmount;
      }

      const rowKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
      const displayName = item.variantName
        ? `${item.productName} • ${item.variantName}`
        : item.productName;

      return {
        Id: rowKey,
        _rowKey: rowKey,
        Name: displayName,
        ProductName: displayName,
        SKU: item.sku || item.SKU || '',
        Quantity: qty,
        Price: unitPriceValue,
        SalePrice: item.salePrice || 0,
        CostPrice: item.costPrice || item.CostPrice || 0,
        VATAmount: vatAmount,
        TotalExcl: totalExcl,
        TotalIncl: totalIncl,
      };
    });
  }, [lineItems, isPriceIncludeVAT, isEUPurchase]);

  const handleDocumentTableRemoveProduct = (product) => {
    const rowKey = product?._rowKey || product?.Id;
    if (!rowKey) return;

    setLineItems((prev) => {
      const nextItems = prev.filter((item, index) => {
        const itemKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
        return itemKey !== rowKey;
      });

      if (nextItems.length !== prev.length) {
        toast.info('Line item removed');
      }

      return nextItems;
    });
  };

  const handleDocumentTableQuantityChange = (product, newQuantity) => {
    const rowKey = product?._rowKey || product?.Id;
    const parsedQuantity = Number(newQuantity);
    if (!rowKey || !Number.isFinite(parsedQuantity) || parsedQuantity < 0.01) return;

    setLineItems((prev) =>
      prev.map((item, index) => {
        const itemKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
        if (itemKey !== rowKey) return item;

        const nextQuantity = Math.max(0.01, Math.min(10000, parsedQuantity));
        return {
          ...item,
          quantity: nextQuantity,
          totalPrice: nextQuantity * Number(item.unitPrice || 0),
        };
      })
    );
  };

  const handleDocumentTablePriceChange = (product, newPrice) => {
    const rowKey = product?._rowKey || product?.Id;
    const parsedPrice = Number(newPrice);
    if (!rowKey || !Number.isFinite(parsedPrice) || parsedPrice < 0) return;

    setLineItems((prev) =>
      prev.map((item, index) => {
        const itemKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
        if (itemKey !== rowKey) return item;

        const nextPrice = Math.max(0, Math.min(100000, parsedPrice));
        
        return {
          ...item,
          unitPrice: nextPrice,
          totalPrice: Number(item.quantity || 0) * nextPrice,
        };
      })
    );
  };

  const handleDocumentTableTotalInclChange = (product, newTotalIncl) => {
    const rowKey = product?._rowKey || product?.Id;
    const parsedTotalIncl = Number(newTotalIncl);
    if (!rowKey || !Number.isFinite(parsedTotalIncl) || parsedTotalIncl < 0) return;

    setLineItems((prev) =>
      prev.map((item, index) => {
        const itemKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
        if (itemKey !== rowKey) return item;

        const qty = Number(item.quantity || 1);
        const taxRateValue = Number(item.taxRate || 0);
        if (qty <= 0) return item;

        let nextUnitPrice;
        if (isPriceIncludeVAT) {
          nextUnitPrice = parsedTotalIncl / qty;
        } else {
          const totalExcl = taxRateValue > 0
            ? parsedTotalIncl / (1 + taxRateValue / 100)
            : parsedTotalIncl;
          nextUnitPrice = totalExcl / qty;
        }

        const safeUnitPrice = Number.isFinite(nextUnitPrice) ? nextUnitPrice : 0;

        return {
          ...item,
          unitPrice: safeUnitPrice,
          totalPrice: qty * safeUnitPrice,
        };
      })
    );
  };

  // ✅ Handle Product Edit from Table
  const handleProductEdit = async (product) => {
    const rowKey = product?._rowKey || product?.Id;
    if (!rowKey) return;

    // Find the corresponding line item
    const lineItem = lineItems.find((item, index) => {
      const itemKey = item._rowKey || `${item.productId}_${item.variantId || 'no_variant'}_${index}`;
      return itemKey === rowKey;
    });

    if (!lineItem) return;

    try {
      // Fetch complete product details
      const response = await getProductById(lineItem.productId);
      const productDetails = response?.payload || response;
      
      setEditingProduct(productDetails);
      setEditingProductLineItem(lineItem);
      setOpenProductDialog(true);
    } catch (error) {
      toast.error('Failed to load product details');
    }
  };

  // ✅ Handle Add Supplier
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierFormTouched(true);
      toast.error('Supplier name is required');
      return;
    }

    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };

      const response = await addSupplier(supplierData);
      const addedSupplier = response?.payload || response;
      toast.success('Supplier added successfully');
      
      // Refresh suppliers list
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      }
      
      // Set the newly added supplier as selected with all necessary fields
      const addedSupplierId = addedSupplier?.Id || addedSupplier?.id;
      if (addedSupplierId) {
        setSelectedSupplier({
          value: addedSupplierId,
          label: addedSupplier.Name || newSupplier.name.trim(),
          Name: addedSupplier.Name || newSupplier.name.trim(),
          Contactperson: addedSupplier.Contactperson || newSupplier.contactPerson,
          Email: addedSupplier.Email || newSupplier.email,
          MobileNumber: addedSupplier.MobileNumber || newSupplier.phone,
          ...addedSupplier
        });
      }
      
      setOpenSupplierDialog(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setSupplierFormTouched(false);

    } catch (error) {

      toast.error(error.response?.data?.message || 'Failed to add supplier');
    }
  };

  // ✅ Handle Add Product
  const handleAddProductFromDialog = async () => {
    if (!newProductData.Name.trim()) {
      setProductFormTouched(true);
      toast.error('Product name is required');
      return;
    }

    try {
      const productData = {
        Name: newProductData.Name.trim(),
        SKU: newProductData.SKU?.trim() || null,
        SalePrice: parseFloat(newProductData.SalePrice) || 0,
        PurchasePrice: parseFloat(newProductData.PurchasePrice) || 0,
        Quantity: 0,
        Description: null,
        CategoryId: null
      };

      const response = await addProduct(productData);
      toast.success('Product added successfully');
      
      // Set the newly added product as selected
      const addedProductId = response.Id || response.id || response.payload?.Id;
      if (addedProductId) {
        setSelectedProduct({
          value: addedProductId,
          label: newProductData.Name.trim(),
          sku: newProductData.SKU?.trim() || ''
        });
        setUnitPrice(newProductData.PurchasePrice || '');
      }
      
      setOpenProductDialog(false);
      setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
      setProductFormTouched(false);

    } catch (error) {

      toast.error(error.response?.data?.message || 'Failed to add product');
    }
  };

  // ✅ Handle File Upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (PDF, images)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setInvoiceFile(file);
      setInvoiceFileName(file.name);
      toast.success(`File ${file.name} uploaded successfully`);
    }
  };

  // ✅ Download Excel Template Handler
  const handleDownloadTemplate = async () => {
    try {
      toast.info('Downloading template...');
      
      const blob = await downloadPurchaseOrderTemplate();
      
      // Verify we received a valid blob
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Invalid response: Expected a file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Purchase_Order_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      // Handle blob error responses
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            toast.error(errorData.message || 'Failed to download template');
          } catch {
            toast.error('Failed to download template');
          }
        };
        reader.readAsText(error.response.data);
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to download template');
      }
    }
  };

  // ✅ Open Import Dialog
  const handleOpenImportDialog = () => {
    setImportDialog({
      open: true,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null,
      successMessage: null,
      selectedNewProductIndices: [],
      activeStep: 0,
      importedLineItems: []
    });
  };

  // ✅ Close Import Dialog
  const handleCloseImportDialog = () => {
    setImportDialog({
      open: false,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null,
      successMessage: null,
      selectedNewProductIndices: [],
      activeStep: 0,
      importedLineItems: []
    });
  };

  // ✅ Toggle individual new product selection
  const handleToggleNewProduct = (index) => {
    setImportDialog(prev => {
      const selected = prev.selectedNewProductIndices;
      const isSelected = selected.includes(index);
      
      return {
        ...prev,
        selectedNewProductIndices: isSelected 
          ? selected.filter(i => i !== index)
          : [...selected, index]
      };
    });
  };

  // ✅ Toggle all new products
  const handleToggleAllNewProducts = () => {
    const newProductsCount = importDialog.validationResult?.payload?.NewProducts?.length || 0;
    const allIndices = Array.from({ length: newProductsCount }, (_, i) => i);
    
    setImportDialog(prev => ({
      ...prev,
      selectedNewProductIndices: prev.selectedNewProductIndices.length === newProductsCount ? [] : allIndices
    }));
  };

  // ✅ Handle File Selection
  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    const errors = [];
    
    if (!file.name.endsWith('.xlsx')) {
      errors.push('Only .xlsx files are supported');
    }
    
    if (file.size > 10 * 1024 * 1024) {
      errors.push('File size exceeds 10MB limit');
    }

    if (errors.length > 0) {
      setImportDialog(prev => ({
        ...prev,
        file: null,
        fileName: '',
        error: errors.join('. '),
        validationResult: null,
        successMessage: null
      }));
      return;
    }

    setImportDialog(prev => ({
      ...prev,
      file,
      fileName: file.name,
      error: null,
      validationResult: null,
      successMessage: null
    }));
  };

  // ✅ Handle Validate Products
  const handleValidateImportFile = async () => {
    if (!importDialog.file) {
      setImportDialog(prev => ({ ...prev, error: 'Please select a file first' }));
      return;
    }

    try {
      setImportDialog(prev => ({ ...prev, validating: true, error: null, successMessage: null }));
      
      const result = await validatePurchaseOrderImportFile(importDialog.file);
      
      // Auto-select all new products by default
      const newProductsCount = result?.payload?.NewProducts?.length || 0;
      const allIndices = Array.from({ length: newProductsCount }, (_, i) => i);
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: result,
        error: null,
        selectedNewProductIndices: allIndices,
        activeStep: 1 // Move to next step on successful validation
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'File validation failed';
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: null,
        error: errorMessage
      }));
    }
  };

  // ✅ Navigate to next step
  const handleNextStep = () => {
    setImportDialog(prev => ({
      ...prev,
      activeStep: prev.activeStep + 1
    }));
  };

  // ✅ Navigate to previous step
  const handlePreviousStep = () => {
    setImportDialog(prev => ({
      ...prev,
      activeStep: prev.activeStep - 1
    }));
  };

  // ✅ Finalize import and add line items to purchase order
  const handleFinalizeImport = () => {
    if (importDialog.importedLineItems.length === 0) {
      toast.error('No products to add');
      return;
    }

    setLineItems(prev => [...prev, ...importDialog.importedLineItems]);
    toast.success(`Added ${importDialog.importedLineItems.length} product${importDialog.importedLineItems.length > 1 ? 's' : ''} to purchase order`);
    handleCloseImportDialog();
  };

  // ✅ Select validated products as line items (without creating new products)
  const handleSelectAsLineItems = () => {
    if (!importDialog.validationResult) {
      setImportDialog(prev => ({ ...prev, error: 'Please validate the file first' }));
      return;
    }

    const validationPayload = importDialog.validationResult?.payload || {};
    const existingProducts = validationPayload.ExistingProducts || [];
    
    if (existingProducts.length === 0) {
      toast.error('No products found to add to line items');
      return;
    }

    // Map existing products to line items
    const newLineItems = existingProducts.map(product => {
      const productData = product.Product || product;
      
      return {
        _rowKey: Date.now() + Math.random(),
        productId: productData.Id || productData.ProductId,
        productName: productData.Name || productData.ProductName,
        sku: productData.SKU,
        quantity: product.Quantity || 1,
        unitPrice: product.UnitCost || productData.CostPrice || 0,
        taxRate: product.TaxPercentage || productData.TaxPercentage || (isEUPurchase ? 0 : taxRate),
        totalPrice: (product.Quantity || 1) * (product.UnitCost || productData.CostPrice || 0),
        description: product.Description || '',
        serialNumbers: []
      };
    });
    
    // Store line items and move to review step
    setImportDialog(prev => ({
      ...prev,
      importedLineItems: newLineItems,
      activeStep: 2,
      successMessage: `${existingProducts.length} product${existingProducts.length > 1 ? 's' : ''} ready to add`
    }));
  };

  // ✅ Import Products Handler (creates new products + adds all to line items)
  const handleImportProducts = async () => {
    if (!importDialog.validationResult) {
      setImportDialog(prev => ({ ...prev, error: 'Please validate the file first' }));
      return;
    }

    try {
      setImportDialog(prev => ({ ...prev, loading: true, error: null, successMessage: null }));
      
      // Prepare confirmation data from validation result
      const validationPayload = importDialog.validationResult?.payload || {};
      
      // Only include selected new products
      const allNewProducts = validationPayload.NewProducts || [];
      const selectedNewProducts = importDialog.selectedNewProductIndices.map(idx => allNewProducts[idx]);
      
      const confirmDto = {
        ExistingProducts: validationPayload.ExistingProducts || [],
        NewProducts: selectedNewProducts
      };
      
      const result = await confirmPurchaseOrderImport(confirmDto);
      
      if (result.success || result.status === 200) {
        const payload = result.payload || result.data || {};
        const lineItems = payload.LineItems || [];
        const selectedNewProductsCount = payload.NewProductsCreated || selectedNewProducts.length;
        const existingProductsCount = payload.ExistingProductsResolved || 0;
        
        // Map line items from API response
        if (lineItems.length > 0) {
          const newLineItems = lineItems.map(item => {
            const productData = item.Product || item;
            
            return {
              _rowKey: Date.now() + Math.random(),
              productId: productData.Id || productData.ProductId,
              productName: productData.Name || productData.ProductName,
              sku: productData.SKU,
              quantity: item.Quantity || 1,
              unitPrice: item.UnitCost || productData.CostPrice || 0,
              taxRate: item.TaxPercentage || productData.TaxPercentage || (isEUPurchase ? 0 : taxRate),
              totalPrice: (item.Quantity || 1) * (item.UnitCost || productData.CostPrice || 0),
              description: item.Description || '',
              serialNumbers: []
            };
          });
          
          // Store line items and move to review step
          setImportDialog(prev => ({
            ...prev,
            loading: false,
            importedLineItems: newLineItems,
            activeStep: 2,
            successMessage: `Successfully imported: ${selectedNewProductsCount} new products created, ${existingProductsCount} existing products added`
          }));
        } else {
          setImportDialog(prev => ({
            ...prev,
            loading: false,
            error: 'No line items returned from import'
          }));
        }
      } else {
        setImportDialog(prev => ({
          ...prev,
          loading: false,
          error: result.message || 'Import failed'
        }));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to import products';
      setImportDialog(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
    }
  };

  const handleSubmitPurchaseInvoice = async (submissionStatus = documentStatus) => {
    // Validation
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    
    if (storeNames.length > 0 && !selectedStore) {
      toast.error('Please select a store');
      return;
    }
    
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    if (!invoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    if (!referenceNumber || !referenceNumber.trim()) {
      setReferenceNumberError(true);
      return;
    }

    // ✅ Validate line items against DTO constraints
    const hasInvalidQuantity = lineItems.some(item => {
      const qty = parseFloat(item.quantity);
      return qty < 0.01 || qty > 10000;
    });
    
    const hasInvalidPrice = lineItems.some(item => {
      const price = parseFloat(item.unitPrice);
      return price < 0 || price > 100000;
    });
    
    if (hasInvalidQuantity) {
      toast.warning('Some quantities are outside valid range (0.01 - 10,000). They will be adjusted.');
    }
    
    if (hasInvalidPrice) {
      toast.warning('Some prices are outside valid range (0 - 100,000). They will be adjusted.');
    }

    // ✅ Prepare form data with individual fields (backend expects [FromForm])
    const formData = new FormData();
    
    // ✅ Add invoice ID if updating existing invoice
    if (editingInvoiceId) {
      formData.append('Id', editingInvoiceId);
    }
    
    // ✅ Append header fields directly
    formData.append('PurchaseInvoiceNumber', invoiceNumber.trim());
    formData.append('InvoiceDate', invoiceDate);
    if (dueDate) formData.append('DueDate', dueDate);
    formData.append('SupplierId', selectedSupplier.value);
    if (selectedStore) formData.append('StoreId', selectedStore.value); // ✅ Required store selection
    formData.append('Status', 'Not Paid'); // Payment status: Not Paid/Partial Paid/Paid
    formData.append('DocumentStatus', submissionStatus); // Document status: Draft or Posted
    formData.append('IsICA', isEUPurchase ? 'true' : 'false'); // EU/Intra-Community Acquisition
    
    // Ensure all monetary values are within valid ranges (0 to 100000) and properly formatted
    const validSubTotal = Math.max(0, Math.min(100000, parseFloat(calculations.subTotal) || 0));
    const validTaxAmount = Math.max(0, Math.min(100000, parseFloat(calculations.taxAmount) || 0));
    const validDiscountAmount = Math.max(0, Math.min(100000, parseFloat(calculations.discountAmount) || 0));
    const validShipmentCost = Math.max(0, Math.min(100000, parseFloat(calculations.shipmentCost) || 0)); // ✅ Validate shipment cost
    const validTotalAmount = Math.max(0, Math.min(100000, parseFloat(calculations.totalAmount) || 0));
    
    formData.append('SubTotal', validSubTotal.toFixed(2));
    formData.append('TaxAmount', validTaxAmount.toFixed(2));
    formData.append('DiscountAmount', validDiscountAmount.toFixed(2));
    formData.append('ShipmentCost', validShipmentCost.toFixed(2)); // ✅ Add shipment cost to form data
    formData.append('TotalAmount', validTotalAmount.toFixed(2));
    if (notes) formData.append('Notes', notes);
    if (referenceNumber) formData.append('ReferenceNumber', referenceNumber);
    
    // ✅ Append line items with indexed notation for [FromForm] binding
    lineItems.forEach((item, index) => {
      // Validate and clamp all values to DTO ranges
      const quantity = Math.max(0.01, Math.min(10000, parseFloat(item.quantity) || 1));
      const unitPrice = Math.max(0, Math.min(100000, parseFloat(item.unitPrice) || 0));
      const discountPercentage = Math.max(0, Math.min(100, parseFloat(item.discountPercentage || 0)));
      const taxRate = Math.max(0, Math.min(100, parseFloat(item.taxRate || 0)));
      
      // Calculate amounts with validation
      const subtotal = quantity * unitPrice;
      // Use fixed discount amount if available, otherwise calculate from percentage
      const discountAmount = item.discountAmount != null && item.discountAmount !== 0
        ? Math.min(100000, Math.max(0, parseFloat(item.discountAmount) || 0))
        : Math.min(100000, Math.max(0, ((subtotal * discountPercentage) / 100) || 0));
      const subtotalAfterDiscount = subtotal - discountAmount;
      
      // Calculate tax based on whether price includes VAT
      let taxAmount, lineTotal;
      if (isPriceIncludeVAT) {
        taxAmount = (subtotalAfterDiscount * taxRate) / (100 + taxRate);
        lineTotal = subtotalAfterDiscount;
      } else {
        taxAmount = (subtotalAfterDiscount * taxRate) / 100;
        lineTotal = subtotalAfterDiscount + taxAmount;
      }
      
      // Ensure amounts don't exceed max ranges
      taxAmount = Math.min(100000, Math.max(0, taxAmount));
      lineTotal = Math.min(100000, Math.max(0, lineTotal));
      
      // Append all fields with proper formatting
      // ✅ Include line item ID if updating existing line item
      if (item.lineItemId) {
        formData.append(`LineItems[${index}].Id`, item.lineItemId);
      }
      
      formData.append(`LineItems[${index}].ProductId`, item.productId);
      if (item.variantId) formData.append(`LineItems[${index}].ProductVariantId`, item.variantId);
      formData.append(`LineItems[${index}].HasVariants`, item.hasVariants || false);
      formData.append(`LineItems[${index}].IsSerializedProduct`, item.isSerializedProduct || false);
      formData.append(`LineItems[${index}].Quantity`, quantity.toFixed(2));
      formData.append(`LineItems[${index}].UnitCost`, unitPrice.toFixed(2));
      formData.append(`LineItems[${index}].DiscountPercentage`, discountPercentage.toFixed(2));
      formData.append(`LineItems[${index}].DiscountAmount`, discountAmount.toFixed(2));
      formData.append(`LineItems[${index}].TaxPercentage`, taxRate.toFixed(2));
      formData.append(`LineItems[${index}].TaxAmount`, taxAmount.toFixed(2));
      formData.append(`LineItems[${index}].LineTotal`, lineTotal.toFixed(2));
      
      if (item.description) {
        const trimmedDesc = item.description.substring(0, 200);
        formData.append(`LineItems[${index}].Description`, trimmedDesc);
      }
      
      // ✅ Append IMEI/Serial inventory items with nested indexed notation
      if (item.isSerializedProduct && item.imeiItems?.length > 0) {
        item.imeiItems.forEach((imeiItem, imeiIndex) => {
          if (imeiItem.imei) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].IMEI`, imeiItem.imei);
          }
          if (imeiItem.serialNumber) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].SerialNumber`, imeiItem.serialNumber);
          }
          if (imeiItem.warrantyExpiryDate) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].WarrantyExpiryDate`, imeiItem.warrantyExpiryDate);
          }
          if (imeiItem.notes) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].Notes`, imeiItem.notes);
          }
        });
      }
    });
    
    // ✅ Add invoice PDF file(s) if uploaded
    if (invoiceFile) {
      formData.append('InvoicePDF', invoiceFile);
    }

    try {
      // ✅ Send formData (backend expects [FromForm])
      // Use update endpoint if editing existing invoice, otherwise create new
      const response = editingInvoiceId 
        ? await updatePurchaseInvoice(formData)
        : await addPurchaseInvoice(formData);
      
      const actionType = editingInvoiceId ? 'updated' : 'created';
      const statusMessage = submissionStatus === 'Posted' 
        ? `Purchase invoice ${actionType} and posted successfully!` 
        : `Purchase invoice ${actionType} as draft successfully!`;
      toast.success(statusMessage);
      
      // ✅ Navigate to purchase detail page
      const invoiceId =  response?.payload || response?.payload?.Id || response?.data?.id || response?.data?.Id || response?.id || response?.Id;
      if (invoiceId) {
        navigate(`/purchases/${invoiceId}`);
      } else {
        navigate('/purchases');
      }
    } catch (error) {

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${editingInvoiceId ? 'update' : 'create'} purchase invoice`);
      }
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        {/* Loading State */}
        {isLoadingInvoice && (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', mb: 3 }}>
            <CircularProgress size={50} />
            <Typography variant="h6" sx={{ mt: 2 }}>Loading purchase invoice...</Typography>
          </Paper>
        )}
        
        {!isLoadingInvoice && (
          <>
            {/* Header Card */}
            <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                        <ReceiptIcon sx={{ color: "#1976d2" }} fontSize="large" />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          {editingInvoiceId 
                            ? (documentStatus === 'Draft' ? 'Continue Draft Purchase Invoice' : 'Edit Purchase Invoice')
                            : 'New Purchase Invoice'
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {editingInvoiceId 
                            ? 'Edit and finalize your purchase order' 
                            : 'Create and manage purchase orders'
                          }
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      {editingInvoiceId && (
                        <Chip
                          label={documentStatus === 'Draft' ? 'DRAFT' : 'EDITING'}
                          color={documentStatus === 'Draft' ? 'warning' : 'info'}
                          size="small"
                          icon={<EditIcon fontSize="small" />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </Box>
                }
                sx={{
                  background: alpha("#1976d2", 0.08),
                  borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
                }}
              />
          <CardContent>
            <Grid container spacing={3}>
              {/* Supplier and Store Selection */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon sx={{ color: "#1976d2" }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Supplier Information *
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenSupplierDialog(true)}
                      sx={{ borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Add New
                    </Button>
                  </Stack>
                  <SupplierSelect
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    loadOptions={loadSupplierOptions}
                    isLoading={suppliersLoading}
                    placeholder="Type at least 3 characters to search..."
                  />
                  
                  {/* Store Selection */}
                  <Box sx={{ mt: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <StoreIcon sx={{ color: "#4caf50" }} />
                        <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                          Store *
                        </Typography>
                      </Stack>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Store</InputLabel>
                        <Select
                          value={selectedStore?.value || ''}
                          label="Select Store"
                          onChange={(e) => {
                            const store = storeNames.find(s => s.Id === e.target.value);
                            if (store) setSelectedStore({ value: store.Id, label: store.StoreName });
                          }}
                          disabled={storesLoading}
                          sx={{ borderRadius: 1.5 }}
                        >
                          {storeNames.map((store) => (
                            <MenuItem key={store.Id} value={store.Id}>
                              {store.StoreName}{store.IsMain ? ' (Main)' : store.IsVirtual ? ' (Virtual)' : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                </Paper>
              </Grid>

              {/* Invoice Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignments="center" spacing={1} mb={2}>
                    <CalendarTodayIcon sx={{ color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Invoice Details
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Invoice Date *"
                        type="date"
                        value={invoiceDate}
                        onChange={e => setInvoiceDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                          label="Reference Number (Supplier's Invoice) *"
                          value={referenceNumber}
                          onChange={e => { setReferenceNumber(e.target.value); setInvoiceNumber(e.target.value); setReferenceNumberError(false); }}
                          size="small"
                          required
                          error={referenceNumberError}
                          helperText={referenceNumberError ? "Supplier's invoice number is required" : ''}
                          placeholder="e.g., SUP-2024-001"
                          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                        <Tooltip
                          title={
                            isEUPurchase
                              ? 'EU Purchase active — all VAT rates set to 0%'
                              : 'Enable for EU suppliers (non-BE VAT): sets all VAT to 0%'
                          }
                          placement="top"
                          arrow
                        >
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: `1px solid ${isEUPurchase ? '#1976d2' : '#e0e0e0'}`,
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.5,
                            bgcolor: isEUPurchase ? alpha('#1976d2', 0.06) : 'transparent',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={isEUPurchase}
                                  onChange={(e) => {
                                    const enabled = e.target.checked;
                                    setIsEUPurchase(enabled);
                                    const newRate = enabled ? 0 : 21;
                                    setTaxRate(newRate);
                                    setLineItems(prev => prev.map(item => ({ ...item, taxRate: newRate })));
                                  }}
                                  color="primary"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                                    EU Purchase
                                  </Typography>
                                  <Typography variant="caption" color={isEUPurchase ? 'primary' : 'text.secondary'} lineHeight={1}>
                                    VAT 0%
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{ borderRadius: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {invoiceFileName || 'Upload Invoice (PDF/Image)'}
                          <input
                            type="file"
                            hidden
                            accept="application/pdf,image/*"
                            onChange={handleFileUpload}
                          />
                        </Button>
                        {invoiceFileName && (
                          <Chip
                            label={invoiceFileName}
                            onDelete={() => {
                              setInvoiceFile(null);
                              setInvoiceFileName('');
                            }}
                            color="primary"
                            size="small"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Line Items Table */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Purchase Line Items
                  </Typography>
                  {lineItems.length > 0 && (
                    <Chip
                      label={`${lineItems.length} item${lineItems.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudDownloadIcon />}
                    onClick={handleDownloadTemplate}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Download Template
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileUploadIcon />}
                    onClick={handleOpenImportDialog}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Import Products
                  </Button>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: isEUPurchase ? '#bdbdbd' : (isPriceIncludeVAT ? '#4caf50' : '#ff9800'),
                      bgcolor: isEUPurchase ? '#f5f5f5' : (isPriceIncludeVAT ? '#e8f5e9' : '#fff3e0'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      opacity: isEUPurchase ? 0.6 : 1,
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: isEUPurchase ? '#9e9e9e' : (isPriceIncludeVAT ? '#2e7d32' : '#f57c00')
                      }}
                    >
                      Price Includes VAT
                    </Typography>
                    <Switch
                      checked={isEUPurchase ? false : isPriceIncludeVAT}
                      onChange={(e) => !isEUPurchase && setIsPriceIncludeVAT(e.target.checked)}
                      color="success"
                      size="small"
                      disabled={isEUPurchase}
                    />
                  </Paper>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenProductDialog(true)}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Add New Product
                  </Button>
                </Box>
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 0 }}>
                <DocumentProductsTable
                  products={purchaseDocumentProducts}
                  onProductAdd={(product, qty, price) => {
                    const productId = product.Id || product.value;
                    const productPrice = Math.max(0, Math.min(100000, Number(price)));
                    const addQuantity = Math.max(0.01, Math.min(10000, Number(qty)));

                    // Validate product data
                    if (!productId) {
                      toast.error('Invalid product selected');
                      return;
                    }

                    if (!addQuantity || addQuantity < 0.01) {
                      toast.error('Quantity must be at least 0.01');
                      return;
                    }

                    if (!productPrice || productPrice < 0) {
                      toast.error('Invalid price');
                      return;
                    }

                    // Check if product with same ID and price already exists
                    const existingItemIndex = lineItems.findIndex(
                      item => item.productId === productId && Number(item.unitPrice) === productPrice
                    );

                    if (existingItemIndex !== -1) {
                      // Product exists with same price - update quantity
                      setLineItems(prev => {
                        const updated = [...prev];
                        const existingItem = updated[existingItemIndex];
                        const newQuantity = existingItem.quantity + addQuantity;
                        const newTotalPrice = newQuantity * productPrice;
                        
                        updated[existingItemIndex] = {
                          ...existingItem,
                          quantity: newQuantity,
                          totalPrice: newTotalPrice,
                        };
                        return updated;
                      });
                      toast.success(`Quantity updated for ${product.Name || product.ProductName}`);
                    } else {
                      // Product doesn't exist or has different price - add new line
                      const validTaxRate = Math.max(0, Math.min(100, Number(taxRate) || 21));
                      const productSalePrice = product.SalePrice || product.Price || 0;
                      const lineItem = {
                        _rowKey: `${Date.now()}_${Math.random()}`,
                        productId: productId,
                        productName: product.Name || product.ProductName || '',
                        sku: product.SKU || '',
                        variantId: null,
                        variantName: null,
                        hasVariants: product.HasVariants || false,
                        isSerializedProduct: product.IsSerializedProduct || false,
                        imeiItems: [],
                        quantity: addQuantity,
                        unitPrice: productPrice,
                        salePrice: productSalePrice,
                        discountPercentage: 0,
                        taxRate: validTaxRate,
                        totalPrice: addQuantity * productPrice,
                      };
                      setLineItems(prev => [...prev, lineItem]);
                      toast.success(`Line item added: ${lineItem.productName}`);
                    }
                  }}
                  onProductRemove={handleDocumentTableRemoveProduct}
                  onQuantityChange={handleDocumentTableQuantityChange}
                  onPriceChange={handleDocumentTablePriceChange}
                  onTotalInclChange={handleDocumentTableTotalInclChange}
                  loadProductOptions={loadProductOptions}
                  onAddProductClick={() => setOpenProductDialog(true)}
                  vatPercentage={taxRate}
                  showVatColumn={true}
                  showTotalExclColumn={true}
                  showTotalInclColumn={true}
                  showAddProductRow={true}
                  showSalePriceColumn={true}
                  showCostPriceColumn={true}
                  onProductEdit={handleProductEdit}
                />
              </Box>

              <Divider />

              {/* Totals Section */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {/* Notes and Discount */}
                    <Stack spacing={2}>
                      <TextField
                        label="Discount Amount"
                        type="number"
                        value={discountAmount}
                        onChange={e => {
                          const value = Number(e.target.value);
                          setDiscountAmount(Math.max(0, Math.min(100000, value)));
                        }}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><DiscountIcon fontSize="small" /></InputAdornment>,
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          max: 100000,
                          step: 0.01
                        }}
                        helperText="Max: €100,000"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <TextField
                        label="Shipment Cost"
                        type="number"
                        value={shipmentCost}
                        onChange={e => {
                          const value = Number(e.target.value);
                          setShipmentCost(Math.max(0, Math.min(100000, value)));
                        }}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">🚚</InputAdornment>,
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          max: 100000,
                          step: 0.01
                        }}
                        helperText="Delivery/Shipping charges (Max: €100,000)"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Add any additional notes..."
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><NotesIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#1976d2", 0.05) }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.subTotal}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Tax Amount:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.taxAmount}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Discount:</Typography>
                          <Typography variant="body1" fontWeight="medium" color="error.main">
                            -€{calculations.discountAmount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Shipment Cost:</Typography>
                          <Typography variant="body1" fontWeight="medium" color="success.main">
                            +€{calculations.shipmentCost}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            €{calculations.totalAmount}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

        {/* Settings and Submit */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/purchases')}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    py: 1.8
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => handleSubmitPurchaseInvoice('Draft')}
                  disabled={!selectedSupplier || lineItems.length === 0}
                  startIcon={<SaveIcon />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    py: 1.8,
                    fontSize: '1rem',
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6,
                    }
                  }}
                >
                  {editingInvoiceId && documentStatus === 'Draft' 
                    ? 'Update Draft' 
                    : 'Save as Draft'
                  }
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── IMEI Entry Dialog ── */}
        <IMEISerialDialog
          open={imeiDialogOpen}
          onClose={() => setImeiDialogOpen(false)}
          quantity={Number(quantity)}
          variantName={selectedVariant?.variantName || selectedProduct?.Name || 'Product'}
          variantId={selectedVariant?.id || null}
          productId={selectedProduct?.Id || selectedProduct?.value}
          existingItems={pendingImeiItems}
          onSave={(items) => {
            setPendingImeiItems(items);
            const prevQuantity = Number(quantity);
            const newQuantity = items.length;
            setQuantity(newQuantity); // ✅ Auto-sync quantity with IMEI count
            
            // Notify user about auto-sync
            if (prevQuantity !== newQuantity) {
              toast.info(`Quantity automatically updated from ${prevQuantity} to ${newQuantity} to match IMEI count`);
            }
            
            setImeiDialogOpen(false);
          }}
        />

        {/* ✅ Supplier Dialog */}
        <Dialog 
          open={openSupplierDialog} 
          onClose={() => {
            setOpenSupplierDialog(false);
            setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
            setSupplierFormTouched(false);
          }} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Supplier Name *"
              fullWidth
              required
              value={newSupplier.name}
              onChange={(e) => {
                setNewSupplier({ ...newSupplier, name: e.target.value });
                if (supplierFormTouched && e.target.value.trim()) {
                  setSupplierFormTouched(false);
                }
              }}
              onBlur={() => {
                if (!newSupplier.name.trim()) {
                  setSupplierFormTouched(true);
                }
              }}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText={supplierFormTouched && !newSupplier.name.trim() ? "Supplier name is required" : "Required field"}
              error={supplierFormTouched && !newSupplier.name.trim()}
            />
            <TextField
              margin="dense"
              label="Contact Person"
              fullWidth
              value={newSupplier.contactPerson || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={newSupplier.email || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Phone"
              fullWidth
              value={newSupplier.phone || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Address"
              fullWidth
              multiline
              rows={2}
              value={newSupplier.address || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setOpenSupplierDialog(false);
                setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
                setSupplierFormTouched(false);
              }}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddSupplier} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 1.5 }}
              disabled={!newSupplier.name.trim()}
            >
              Add Supplier
            </Button>
          </DialogActions>
        </Dialog>

        {/* ✅ AddProductDialog for consistency and editing */}
        <AddProductDialog
          open={openProductDialog}
          onClose={() => {
            setOpenProductDialog(false);
            setEditingProduct(null);
            setEditingProductLineItem(null);
          }}
          initialData={editingProduct}
          onProductCreated={(updatedProduct) => {
            if (editingProductLineItem) {
              // Update existing line item with new product data
              setLineItems(prev => prev.map(item => {
                if (item._rowKey === editingProductLineItem._rowKey) {
                  return {
                    ...item,
                    productName: updatedProduct.Name,
                    sku: updatedProduct.SKU || '',
                    salePrice: updatedProduct.SalePrice || updatedProduct.Price || 0,
                  };
                }
                return item;
              }));
              toast.success('Product updated successfully');
            } else {
              // Set the newly added product as selected
              setSelectedProduct({
                value: updatedProduct.Id || updatedProduct.id,
                label: updatedProduct.Name,
                Name: updatedProduct.Name,
                SKU: updatedProduct.SKU,
                ...updatedProduct
              });
              toast.success('Product added successfully');
            }
            setOpenProductDialog(false);
            setEditingProduct(null);
            setEditingProductLineItem(null);
          }}
        />

        {/* ✅ Import Products Dialog */}
        <Dialog 
          open={importDialog.open} 
          onClose={(event, reason) => {
            // Prevent closing by clicking outside or pressing escape
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return;
            }
            handleCloseImportDialog();
          }}
          maxWidth="lg"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileUploadIcon color="primary" />
                <Typography variant="h6">Import Purchase Order Products</Typography>
              </Box>
              <IconButton onClick={handleCloseImportDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <Divider />
          
          <DialogContent sx={{ pt: 3, minHeight: '500px', overflow: 'hidden' }}>
            {/* Vertical Stepper */}
            <Box sx={{ display: 'flex', gap: 3, height: '100%' }}>
              <Box sx={{ minWidth: '200px', flexShrink: 0 }}>
                <Stepper activeStep={importDialog.activeStep} orientation="vertical">
                  <Step>
                    <StepLabel>Upload & Validate File</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>
                      {importDialog.selectedNewProductIndices.length > 0 ? 'Import Products' : 'Select Products'}
                    </StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Review & Confirm</StepLabel>
                  </Step>
                </Stepper>
              </Box>
              
              <Divider orientation="vertical" flexItem />
              
              {/* Step Content */}
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                {/* Step 0: Upload & Validate */}
                {importDialog.activeStep === 0 && (
                  <>
                    {/* Instructions */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                        📋 Import Instructions:
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ color: '#0d47a1' }}>
                        • Download the template first and fill in your product data<br />
                        • Only .xlsx files are supported (max 10MB)<br />
                        • Products with existing SKU will be updated<br />
                        • Products without SKU will be created as new<br />
                        • All imported products will be added to your purchase order
                      </Typography>
                    </Box>

                    {/* File Upload Area */}
                    <Box sx={{ 
                      border: '2px dashed',
                      borderColor: importDialog.file ? 'success.main' : 'grey.300',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      bgcolor: importDialog.file ? 'success.50' : 'grey.50',
                      mb: 3,
                      transition: 'all 0.3s'
                    }}>
                      <input
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        id="import-file-upload"
                        type="file"
                        onChange={handleImportFileChange}
                        disabled={importDialog.loading || importDialog.validating}
                      />
                      <label htmlFor="import-file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<FileUploadIcon />}
                          disabled={importDialog.loading || importDialog.validating}
                          sx={{ mb: 2 }}
                        >
                          Select File
                        </Button>
                      </label>
                      
                      {importDialog.fileName && (
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                          📄 {importDialog.fileName}
                        </Typography>
                      )}
                      
                      {!importDialog.fileName && (
                        <Typography variant="body2" color="text.secondary">
                          Click to select an Excel file (.xlsx)
                        </Typography>
                      )}
                    </Box>

                    {/* Validation Status */}
                    {importDialog.validating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2">Validating file...</Typography>
                      </Box>
                    )}

                    {/* Error Display */}
                    {importDialog.error && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '1px solid #f44336' }}>
                        <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>
                          ❌ {importDialog.error}
                        </Typography>
                      </Box>
                    )}

                    {/* Validation Result - Summary Only */}
                    {importDialog.validationResult && (
                      <Paper 
                        elevation={3}
                        sx={{ 
                          mb: 3, 
                          p: 3, 
                          bgcolor: importDialog.validationResult.payload?.InvalidRows > 0 ? '#fff3e0' : '#e8f5e9',
                          border: '2px solid',
                          borderColor: importDialog.validationResult.payload?.InvalidRows > 0 ? '#ff9800' : '#4caf50'
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
                            ✓ Validation Results
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {importDialog.validationResult.payload?.Summary || 'File validated successfully'}
                          </Typography>
                        </Box>

                        {importDialog.validationResult.payload && (
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                                  {importDialog.validationResult.payload.TotalRows || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                  Total
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                  {importDialog.validationResult.payload.ValidRows || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                  Valid
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                                  {importDialog.validationResult.payload.NewProducts?.length || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                  New
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                  {importDialog.validationResult.payload.ExistingProducts?.length || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                  Existing
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        )}

                        {importDialog.validationResult.payload?.Errors && importDialog.validationResult.payload.Errors.length > 0 && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#c62828' }}>⚠️ Validation Errors:</Typography>
                            {importDialog.validationResult.payload.Errors.slice(0, 10).map((error, idx) => (
                              <Typography key={idx} variant="caption" component="div" sx={{ color: '#d32f2f' }}>
                                • Row {error.Row}: {error.Field} - {error.Error} {error.ProductIdentifier && `(${error.ProductIdentifier})`}
                              </Typography>
                            ))}
                            {importDialog.validationResult.payload.Errors.length > 10 && (
                              <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic', color: '#757575' }}>
                                ... and {importDialog.validationResult.payload.Errors.length - 10} more errors
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Paper>
                    )}
                  </>
                )}

                {/* Step 1: Import/Select Products */}
                {importDialog.activeStep === 1 && importDialog.validationResult && (
                  <>
                    {/* Product Details */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
                        Product Details:
                      </Typography>
                      
                      {/* New Products */}
                      {importDialog.validationResult.payload.NewProducts && importDialog.validationResult.payload.NewProducts.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196f3' }}>
                              ✨ New Products ({importDialog.validationResult.payload.NewProducts.length}) - Select to Create:
                            </Typography>
                            <Button 
                              size="small" 
                              onClick={handleToggleAllNewProducts}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              {importDialog.selectedNewProductIndices.length === importDialog.validationResult.payload.NewProducts.length 
                                ? 'Deselect All' 
                                : 'Select All'}
                            </Button>
                          </Box>
                          <Box sx={{ 
                            maxHeight: '300px', 
                            overflowY: 'auto', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1, 
                            p: 1,
                            bgcolor: '#f9fafb'
                          }}>
                            {importDialog.validationResult.payload.NewProducts.map((prod, idx) => (
                              <FormControlLabel
                                key={idx}
                                control={
                                  <Checkbox
                                    checked={importDialog.selectedNewProductIndices.includes(idx)}
                                    onChange={() => handleToggleNewProduct(idx)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                    <strong>{prod.ProductName}</strong> {prod.SKU && `(SKU: ${prod.SKU})`} - Qty: {prod.Quantity}, Price: €{prod.UnitCost?.toFixed(2)}
                                  </Typography>
                                }
                                sx={{ 
                                  display: 'flex', 
                                  width: '100%',
                                  m: 0,
                                  mb: 0.5,
                                  p: 1,
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#e3f2fd' }
                                }}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                            {importDialog.selectedNewProductIndices.length} of {importDialog.validationResult.payload.NewProducts.length} selected
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Existing Products */}
                      {importDialog.validationResult.payload.ExistingProducts && importDialog.validationResult.payload.ExistingProducts.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
                            🔄 Existing Products ({importDialog.validationResult.payload.ExistingProducts.length}):
                          </Typography>
                          <Box component="ul" sx={{ pl: 3, m: 0, maxHeight: '200px', overflowY: 'auto' }}>
                            {importDialog.validationResult.payload.ExistingProducts.map((prod, idx) => (
                              <Typography key={idx} component="li" variant="caption" sx={{ mb: 0.5 }}>
                                {prod.Product?.Name || prod.ProductName} {prod.Product?.SKU && `(SKU: ${prod.Product.SKU})`} - Qty: {prod.Quantity}, Price: €{prod.UnitCost?.toFixed(2)}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Warning if no products available */}
                    {(importDialog.validationResult?.payload?.ExistingProducts?.length || 0) === 0 && 
                     importDialog.selectedNewProductIndices.length === 0 && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ff9800', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ color: '#f57c00' }} />
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                          No products available. Please select at least one new product or go back to upload a different file.
                        </Typography>
                      </Box>
                    )}

                    {/* Success Message */}
                    {importDialog.successMessage && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #4caf50', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" sx={{ color: '#1b5e20', fontWeight: 600 }}>
                          {importDialog.successMessage}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* Step 2: Review & Confirm */}
                {importDialog.activeStep === 2 && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
                      Review Imported Products
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
                      Please review the products below. Click "Select as Purchase Order Lineitem" to add them to your purchase order.
                    </Typography>

                    {/* Success Message */}
                    {importDialog.successMessage && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #4caf50', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" sx={{ color: '#1b5e20', fontWeight: 600 }}>
                          {importDialog.successMessage}
                        </Typography>
                      </Box>
                    )}

                    {/* Line Items Table */}
                    {importDialog.importedLineItems.length > 0 && (
                      <Paper elevation={2} sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#495057' }}>
                            Products to Add ({importDialog.importedLineItems.length})
                          </Typography>
                        </Box>
                        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '2px solid #dee2e6' }}>
                              <tr>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>#</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>Product Name</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>SKU</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>Quantity</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>Unit Price</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>Tax %</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importDialog.importedLineItems.map((item, index) => (
                                <tr key={item._rowKey || index} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#495057' }}>{index + 1}</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#212529', fontWeight: 500 }}>{item.productName}</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#6c757d' }}>{item.sku || '-'}</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#212529', textAlign: 'right' }}>{item.quantity}</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#212529', textAlign: 'right' }}>€{item.unitPrice?.toFixed(2)}</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#212529', textAlign: 'right' }}>{item.taxRate}%</td>
                                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#212529', fontWeight: 600, textAlign: 'right' }}>€{item.totalPrice?.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057' }}>
                            Total Products: {importDialog.importedLineItems.length}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#212529' }}>
                            Total: €{importDialog.importedLineItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1, gap: 2, justifyContent: 'space-between' }}>
            <Button 
              onClick={handleCloseImportDialog}
              disabled={importDialog.loading || importDialog.validating}
              variant="outlined"
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Back Button - Show on step 1 and step 2 */}
              {(importDialog.activeStep === 1 || importDialog.activeStep === 2) && (
                <Button 
                  variant="outlined"
                  onClick={handlePreviousStep}
                  disabled={importDialog.loading}
                >
                  Back
                </Button>
              )}
              
              {/* Validate Button - Show on step 0 */}
              {importDialog.activeStep === 0 && (
                <Button 
                  variant="contained"
                  onClick={handleValidateImportFile}
                  disabled={!importDialog.file || importDialog.loading || importDialog.validating || (importDialog.validationResult?.payload?.Errors?.length > 0)}
                  startIcon={importDialog.validating ? <CircularProgress size={20} /> : <WarningIcon />}
                >
                  {importDialog.validating ? 'Validating...' : 'Validate & Continue'}
                </Button>
              )}
              
              {/* Import/Select Button - Show on step 1 */}
              {importDialog.activeStep === 1 && (
                <Button 
                  variant="contained"
                  onClick={() => {
                    // If no new products selected, just select as line items
                    if (importDialog.selectedNewProductIndices.length === 0) {
                      handleSelectAsLineItems();
                    } else {
                      handleImportProducts();
                    }
                  }}
                  disabled={
                    importDialog.loading || 
                    !importDialog.validationResult || 
                    (importDialog.validationResult?.payload?.Errors?.length > 0) ||
                    !!importDialog.error ||
                    // At least one product must be available (new selected OR existing)
                    (importDialog.selectedNewProductIndices.length === 0 && 
                     (importDialog.validationResult?.payload?.ExistingProducts?.length || 0) === 0)
                  }
                  startIcon={importDialog.loading ? <CircularProgress size={20} /> : (importDialog.selectedNewProductIndices.length === 0 ? <CheckCircleIcon /> : <FileUploadIcon />)}
                >
                  {importDialog.loading ? 'Importing...' : (importDialog.selectedNewProductIndices.length === 0 ? 'Select as Purchase Order Lineitem' : 'Import Products')}
                </Button>
              )}

              {/* Finalize Button - Show on step 2 */}
              {importDialog.activeStep === 2 && (
                <Button 
                  variant="contained"
                  onClick={handleFinalizeImport}
                  disabled={importDialog.importedLineItems.length === 0}
                  startIcon={<CheckCircleIcon />}
                  color="success"
                >
                  Select as Purchase Order Lineitem
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>
        </>
        )}
      </Container>
    </Box>
  );
}

export default AddPurchasePage;